/** /tag-categories — tenant-scoped tag-category catalog (grid). */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Chip, MenuItem, Stack, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as tagCategoriesApi from '@/api/tagCategories';
import type { TagCategoryListItem } from '@/api/tagCategories';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { TagCategoryCreateDialog } from './TagCategoryCreateDialog';

const passthrough = (v: string) => v || undefined;

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'is_multitag', toApi: passthrough },
  { param: 'external_identity', toApi: passthrough },
];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

const DEFAULT_ORDER = [
  'id',
  'tenant',
  'code',
  'name',
  'schema',
  'is_multitag',
  'external_identity',
  'tag_count',
  'updated_ts',
];
const DEFAULT_HIDDEN: string[] = [];

const SOURCE_COLOR: Record<string, 'default' | 'success' | 'warning'> = {
  ref: 'success',
  inline: 'warning',
  none: 'default',
};
const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString() : '—');

export function TagCategoriesListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_tag_categories_manage');
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));

  const presets = useGridPresets({
    grid: 'tag_categories',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'tenant',
    filters: PRESET_FILTERS,
  });

  const { extra, getParam, setParam, resetAll, anyActive } = useGridFilters(
    FILTERS,
    presets.filtersLayer,
  );

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what the create dialog pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const multitag = getParam('is_multitag') ?? 'all';
  const extIdentity = getParam('external_identity') ?? 'all';

  const columns = useMemo<GridColDef<TagCategoryListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 160,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.code ?? '',
        renderCell: (p) => (
          <TenantLinkCell
            tenant={p.row.tenant}
            empty={<Chip size="small" variant="outlined" label="system" />}
          />
        ),
      },
      copyableColumn({ field: 'code', headerName: 'Code', width: 160 }),
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        renderCell: (p) => (
          <RouterLink
            to={`/tag-categories/${p.row.id}`}
            style={{ color: '#0a1a3f', fontWeight: 500 }}
          >
            {p.row.name}
          </RouterLink>
        ),
      },
      {
        field: 'schema',
        headerName: 'Schema',
        width: 200,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.schema_source,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              size="small"
              color={SOURCE_COLOR[p.row.schema_source] ?? 'default'}
              variant="outlined"
              label={p.row.schema_source}
            />
            {p.row.schema_ref && (
              <RouterLink
                to={`/tag-schemas/${p.row.schema_ref.id}/schema`}
                style={{ color: '#0a1a3f', fontSize: 12 }}
                onClick={(e) => e.stopPropagation()}
              >
                {p.row.schema_ref.codename}
              </RouterLink>
            )}
          </Stack>
        ),
      },
      {
        field: 'is_multitag',
        headerName: 'Multitag',
        width: 100,
        sortable: false,
        valueGetter: (_, row) => row.is_multitag,
        renderCell: (p) => (p.row.is_multitag ? '✓' : '—'),
      },
      {
        field: 'external_identity',
        headerName: 'Ext. identity',
        width: 120,
        sortable: false,
        description:
          "Tags here are identified by their External ID, not by name — so two may share a display name.",
        valueGetter: (_, row) => row.external_identity,
        renderCell: (p) => (p.row.external_identity ? '✓' : '—'),
      },
      { field: 'tag_count', headerName: 'Tags', width: 90 },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 130,
        valueGetter: (_, row) => row.updated_ts,
        renderCell: (p) => fmtDate(p.row.updated_ts),
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      tagCategoriesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'tenant',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'is_multitag',
      priority: 4,
      active: multitag !== 'all',
      node: (
        <TextField
          select
          size="small"
          label="Multitag"
          value={multitag}
          sx={{ width: 150 }}
          onChange={(e) =>
            setParam('is_multitag', e.target.value === 'all' ? null : e.target.value)
          }
        >
          <MenuItem value="all">Any</MenuItem>
          <MenuItem value="true">Multitag</MenuItem>
          <MenuItem value="false">Single</MenuItem>
        </TextField>
      ),
    },
    {
      key: 'external_identity',
      priority: 3,
      active: extIdentity !== 'all',
      node: (
        <TextField
          select
          size="small"
          label="Identity"
          value={extIdentity}
          sx={{ width: 170 }}
          onChange={(e) =>
            setParam('external_identity', e.target.value === 'all' ? null : e.target.value)
          }
        >
          <MenuItem value="all">Any</MenuItem>
          <MenuItem value="true">By external ID</MenuItem>
          <MenuItem value="false">By name</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="tag-categories"
        title="Tag Categories"
        subtitle="Tenant-scoped tag categories. Prefer a reusable schema over an inline one."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New category
            </Button>
          )
        }
      />

      <TagCategoryCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pageTenants={pageTenants}
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<TagCategoryListItem>
        queryKey={['tag_categories']}
        queryFn={queryFn}
        columns={orderColumns(columns, presets.orderedFields)}
        extra={extra}
        defaultSort={{ field: 'name', sort: 'asc' }}
        sortModel={presets.sortModel}
        onSortModelChange={presets.onSortModelChange}
        columnVisibilityModel={presets.columnVisibilityModel}
        onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
        onColumnOrderChange={presets.onColumnOrderChange}
        emptyMessage="No tag categories yet."
        headerToolbar={{
          onResetAllFilters: resetAll,
          resetDisabled: !anyActive,
          search: true,
          searchPlaceholder: 'Code or name',
          exportFileName: 'tag-categories',
          presets: presets.toolbar,
        }}
      />
    </Box>
  );
}
